import { motion } from "framer-motion";
import { Plane, Briefcase, Map, Truck, ClipboardList, ShieldCheck, Phone } from "lucide-react";
import LeadCaptureForm from "../components/LeadCaptureForm";
import StickyLeadModal from "../components/StickyLeadModal";
import { useSEO } from "../lib/useSEO";
import { BRAND, SEO_KEYWORDS } from "../lib/brandConfig";

const PILLARS = [
  { icon: Plane,       title: "Out-of-State Relocations", body: "Moving to Houston from out of state? We send video tours and handle leasing remotely." },
  { icon: Briefcase,   title: "Corporate Transfers",      body: "Streamlined for HR teams: bulk lookups, executive housing, short-term stays." },
  { icon: Map,         title: "Neighborhood Matching",    body: "Schools, commute, lifestyle — we match the right Houston neighborhood to your life." },
  { icon: Truck,       title: "Moving Logistics",         body: "Trusted movers, storage, utility set-up — vetted partners across Houston metro." },
  { icon: ClipboardList,title: "Application Prep",         body: "Documentation, credit-screening tips, and landlord references that close the deal." },
  { icon: ShieldCheck, title: "Tour Day Concierge",       body: "Pre-booked back-to-back tours, transit, and on-site advocacy when you arrive." },
];

const RelocationPage = () => {
  useSEO({
    title: "Houston Relocation Services | Out-of-State & Corporate Moves | Urban Nest Quest",
    description:
      "Free Houston relocation services from Urban Nest Quest. We handle neighborhood matching, virtual tours, application prep, and tour-day concierge for renters moving to Houston.",
    keywords: SEO_KEYWORDS.relocation,
    path: "/relocation-services",
  });

  return (
    <div className="bg-[#FAFAF7] pt-20" data-testid="relocation-page">
      {/* Hero */}
      <section className="bg-[#1F2A44] text-white">
        <div className="container-main py-12 sm:py-16">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                      className="max-w-3xl">
            <p className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] uppercase tracking-widest text-[#C8A96B] border border-[#C8A96B]/40 font-semibold">
              <Plane className="w-3.5 h-3.5" /> Houston Relocation
            </p>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold font-['Playfair_Display'] leading-tight">
              Moving to Houston?
              <br /><span className="text-[#C8A96B]">We make it effortless.</span>
            </h1>
            <p className="mt-4 text-base sm:text-lg text-white/80 max-w-2xl">
              From the first video tour to your move-in day, Urban Nest Quest handles the Houston end so you can focus on everything else. Whether you're relocating for a job, a family change, or just chasing better weather, we've placed renters from every corner of the country.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pillars */}
      <section className="container-main py-12 sm:py-16">
        <div className="max-w-2xl">
          <p className="text-[11px] uppercase tracking-widest text-[#C8A96B] font-bold">What's included</p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#1F2A44] font-['Playfair_Display']">
            Full relocation support — no cost to you
          </h2>
          <p className="mt-3 text-[#333333]/80">
            Properties pay us a finder's fee. You pay nothing. That means our incentive is finding the right fit, not pushing you into the first available unit.
          </p>
        </div>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PILLARS.map((p) => (
            <div key={p.title}
                 className="p-5 rounded-xl border border-[#D9D9D9] bg-white hover:border-[#C8A96B] transition-colors"
                 data-testid={`pillar-${p.title.toLowerCase().replace(/\s+/g, '-')}`}>
              <p.icon className="w-6 h-6 text-[#C8A96B]" />
              <h3 className="mt-3 text-base font-bold text-[#1F2A44]">{p.title}</h3>
              <p className="mt-1 text-sm text-[#333333]/75">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Lead form */}
      <section className="bg-white border-y border-[#D9D9D9]">
        <div className="container-main py-12 sm:py-16">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1F2A44] font-['Playfair_Display']">
                Start your Houston move
              </h2>
              <p className="mt-3 text-[#333333]/80 max-w-lg">
                Tell us your move date, budget, and what matters most. We'll send a relocation playbook tailored to your destination neighborhood within one business day.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <a href={BRAND.phoneHref}
                   className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#1F2A44] hover:bg-[#C8A96B] text-white font-bold transition-colors"
                   data-testid="reloc-cta-call">
                  <Phone className="w-4 h-4" /> Call {BRAND.phone}
                </a>
                <a href={BRAND.emailHref}
                   className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-[#1F2A44] text-[#1F2A44] hover:bg-[#1F2A44] hover:text-white font-semibold transition-colors"
                   data-testid="reloc-cta-email">
                  Email us
                </a>
              </div>
            </div>
            <LeadCaptureForm source="relocation_services" submitLabel="Get My Relocation Playbook" />
          </div>
        </div>
      </section>

      <StickyLeadModal source="relocation_services_sticky" storageKey="unq_sticky_relocation_v1" />
    </div>
  );
};

export default RelocationPage;
